import { mediaIdImageField } from '@/lib/mediaImageField';
import { DiscountDisplay } from '@/components/promotional-offers/DiscountDisplay';
import {
  DiscountTypeSelector,
  type DiscountType,
} from '@/components/promotional-offers/DiscountTypeSelector';
import { PromotionalOfferImageCell } from '@/components/promotional-offers/PromotionalOfferImageCell';
import { PromotionalOfferStatusBadge } from '@/components/promotional-offers/PromotionalOfferStatusBadge';
import { RedemptionTypeDisplay } from '@/components/promotional-offers/RedemptionTypeDisplay';
import { RedemptionTypeSelector } from '@/components/promotional-offers/RedemptionTypeSelector';
import { BusinessHoursDisplay } from '@/components/shared/BusinessHoursDisplay';
import { BusinessHoursEditor } from '@/components/shared/BusinessHoursEditor';
import { Input } from '@startupquickstart/frontend';
import type { CrudConfig } from '@startupquickstart/frontend';
import { z } from 'zod';

/**
 * Zod schemas mirroring the server-side validation in promotional-offers.schemas.ts.
 * CrudFormView runs these before submitting so server validation errors never occur.
 */
const createPromotionalOfferSchema = z.object({
  name: z.string().min(1).max(200).nullish(),
  title: z.string().min(1).max(200).nullish(),
  description: z.string().nullish(),
  status: z.enum(['published', 'draft']).nullish(),
  featuredImageId: z.string().uuid().nullish(),
  hours: z.any().nullish(),
  termsAndConditions: z.string().nullish(),
  promotionalCode: z.string().nullish(),
  discountPercentage: z.number().min(0).max(100).nullish(),
  minUsers: z.number().int().min(0).nullish(),
  maxUsers: z.number().int().min(0).nullish(),
  maxUserRedemptions: z.number().int().min(0).nullish(),
  maxRedemptions: z.number().int().min(0).nullish(),
  discountType: z.string().nullish(),
  discountValue: z.number().min(0).nullish(),
  maxDiscountAmount: z.number().min(0).nullish(),
  validFrom: z.string().datetime().nullish(),
  validUntil: z.string().datetime().nullish(),
  isActive: z.boolean().nullish(),
  usageLimit: z.number().int().min(0).nullish(),
  redemptionType: z.enum(['self', 'barcode']).nullish(),
});

const updatePromotionalOfferSchema = createPromotionalOfferSchema.partial();

// Define type based on our Prisma schema
export interface PromotionalOffer {
  id: string;
  organizationId: string;
  name?: string;
  title?: string;
  description?: string;
  status?: 'published' | 'draft';
  featuredImageId?: string;
  hours?: Record<string, unknown>; // Business hours JSON (same structure as amenities)
  termsAndConditions?: string;
  promotionalCode?: string;
  discountPercentage?: number;
  minUsers?: number;
  maxUsers?: number;
  maxUserRedemptions?: number;
  maxRedemptions?: number;
  currentRedemptions?: number;
  qrCodeId?: string;
  discountType?: string;
  discountValue?: number;
  maxDiscountAmount?: number;
  validFrom?: string;
  validUntil?: string;
  isActive?: boolean;
  usageLimit?: number;
  usageCount?: number;
  redemptionType?: 'self' | 'barcode';
  createdAt: string;
  updatedAt: string;
  // Relations for display
  organization?: {
    id: string;
    name: string;
  };
  featuredImage?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
}

/**
 * Promotional Offer CRUD configuration
 */
export const promotionalOfferCrudConfig: CrudConfig<PromotionalOffer> = {
  entityName: 'Promotional Offer',
  entityNamePlural: 'Promotional Offers',

  endpoints: {
    list: '/promotional-offers',
    single: (id: string) => `/promotional-offers/${id}`,
    create: '/promotional-offers',
    update: (id: string) => `/promotional-offers/${id}`,
    delete: (id: string) => `/promotional-offers/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: false,
      placeholder: 'Enter promotional offer name',
      helpText: 'Internal name for this offer (not shown to users)',
      validation: {
        max: 200,
      },
    },
    {
      key: 'title',
      label: 'Title',
      type: 'text',
      required: false,
      placeholder: 'Enter promotional offer title',
      helpText: 'Public-facing title displayed to users',
      validation: {
        max: 200,
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter description',
      helpText: 'Detailed description of the offer shown to users',
      validation: {
        max: 2000,
      },
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: false,
      defaultValue: 'published',
      helpText: 'Draft offers are not visible to users',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
      ],
      format: (value: 'published' | 'draft') => {
        if (!value) return null;
        return <PromotionalOfferStatusBadge status={value} />;
      },
    },
    {
      key: 'promotionalCode',
      label: 'Promotional Code',
      type: 'text',
      required: false,
      placeholder: 'e.g., SUMMER25',
      helpText: 'Optional code users can enter to apply this offer',
    },
    {
      key: 'discountType',
      label: 'Discount Type',
      type: 'custom',
      required: true,
      defaultValue: 'percentage',
      helpText: 'Choose how the discount is calculated',
      format: (_value: string | undefined, fullRecord?: PromotionalOffer) => (
        <DiscountDisplay
          discountType={fullRecord?.discountType as 'percentage' | 'fixed_amount' | undefined}
          discountPercentage={fullRecord?.discountPercentage}
          discountValue={fullRecord?.discountValue}
          maxDiscountAmount={fullRecord?.maxDiscountAmount}
        />
      ),
      inputFormat: (value: DiscountType | undefined, onChange: (value: DiscountType) => void) => (
        <DiscountTypeSelector value={value ?? 'percentage'} onChange={onChange} />
      ),
      onFieldChange: (
        newValue: DiscountType,
        setField: (key: keyof PromotionalOffer, value: unknown) => void,
      ) => {
        // Clear irrelevant values when switching discount types
        if (newValue === 'percentage') {
          setField('discountValue', undefined);
        } else if (newValue === 'fixed_amount') {
          setField('discountPercentage', undefined);
          setField('maxDiscountAmount', undefined);
        }
      },
    },
    {
      key: 'discountPercentage',
      label: 'Discount Percentage',
      type: 'number',
      required: false,
      helpText: 'Enter a value between 1 and 100',
      validation: {
        min: 1,
        max: 100,
      },
      inputFormat: (
        value: number | undefined,
        onChange: (value: number | undefined) => void,
        _field: unknown,
        formData?: Record<string, unknown>,
      ) => {
        const discountType = (formData?.discountType as string) ?? 'percentage';
        if (discountType !== 'percentage') return null;

        return (
          <div className="relative">
            <Input
              id="discountPercentage"
              type="number"
              min={1}
              max={100}
              step={1}
              placeholder="e.g., 20"
              value={value ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                onChange(val === '' ? undefined : Number(val));
              }}
              className="pr-8"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              %
            </span>
          </div>
        );
      },
    },
    {
      key: 'maxDiscountAmount',
      label: 'Maximum Discount Cap (optional)',
      type: 'number',
      required: false,
      helpText: 'Limit the maximum discount amount (e.g., 20% off up to $50)',
      validation: {
        min: 0,
      },
      inputFormat: (
        value: number | undefined,
        onChange: (value: number | undefined) => void,
        _field: unknown,
        formData?: Record<string, unknown>,
      ) => {
        const discountType = (formData?.discountType as string) ?? 'percentage';
        if (discountType !== 'percentage') return null;

        return (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              $
            </span>
            <Input
              id="maxDiscountAmount"
              type="number"
              min={0}
              step={0.01}
              placeholder="e.g., 50.00"
              value={value ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                onChange(val === '' ? undefined : Number(val));
              }}
              className="pl-7"
            />
          </div>
        );
      },
    },
    {
      key: 'discountValue',
      label: 'Discount Amount',
      type: 'number',
      required: false,
      helpText: 'Enter the fixed dollar amount to discount',
      validation: {
        min: 0,
      },
      inputFormat: (
        value: number | undefined,
        onChange: (value: number | undefined) => void,
        _field: unknown,
        formData?: Record<string, unknown>,
      ) => {
        const discountType = (formData?.discountType as string) ?? 'percentage';
        if (discountType !== 'fixed_amount') return null;

        return (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              $
            </span>
            <Input
              id="discountValue"
              type="number"
              min={0}
              step={0.01}
              placeholder="e.g., 10.00"
              value={value ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                onChange(val === '' ? undefined : Number(val));
              }}
              className="pl-7"
            />
          </div>
        );
      },
    },
    {
      key: 'hours',
      label: 'Valid Hours',
      type: 'custom',
      required: false,
      helpText: 'Configure which days and times this offer is valid (e.g., lunch specials, happy hours)',
      format: (value: Record<string, unknown> | undefined) => (
        <BusinessHoursDisplay value={value} label24_7="All Day" labelClosed="Not Valid" />
      ),
      inputFormat: (
        value: Record<string, unknown> | undefined,
        onChange: (value: Record<string, unknown> | undefined) => void,
      ) => (
        <BusinessHoursEditor
          value={value}
          onChange={onChange}
          label24_7="All Day"
          labelClosed="Not Valid"
        />
      ),
    },
    {
      key: 'validFrom',
      label: 'Valid From',
      type: 'datetime',
      required: false,
      helpText: 'Exact date/time when users can start redeeming this offer',
    },
    {
      key: 'validUntil',
      label: 'Valid Until',
      type: 'datetime',
      required: false,
      helpText: 'Exact date/time when this offer expires and can no longer be redeemed',
    },
    {
      key: 'minUsers',
      label: 'Minimum Group Size',
      type: 'number',
      required: false,
      placeholder: 'e.g., 2',
      helpText: 'Minimum number of people required to use this offer',
      validation: {
        min: 0,
      },
    },
    {
      key: 'maxUsers',
      label: 'Maximum Group Size',
      type: 'number',
      required: false,
      placeholder: 'e.g., 10',
      helpText: 'Maximum number of people allowed for this offer',
      validation: {
        min: 0,
      },
    },
    {
      key: 'maxUserRedemptions',
      label: 'Max Redemptions Per User',
      type: 'number',
      required: false,
      placeholder: 'e.g., 1',
      helpText: 'How many times a single user can redeem this offer (leave empty for unlimited)',
      validation: {
        min: 0,
      },
    },
    {
      key: 'maxRedemptions',
      label: 'Max Total Redemptions',
      type: 'number',
      required: false,
      placeholder: 'e.g., 100',
      helpText: 'Total number of times this offer can be redeemed across all users',
      validation: {
        min: 0,
      },
    },
    {
      key: 'usageLimit',
      label: 'Usage Limit',
      type: 'number',
      required: false,
      placeholder: 'e.g., 500',
      helpText: 'Maximum number of times this offer can be used in total',
      validation: {
        min: 0,
      },
    },
    {
      key: 'isActive',
      label: 'Is Active',
      type: 'boolean',
      required: false,
      defaultValue: true,
      helpText: 'Inactive offers cannot be redeemed even if published',
    },
    {
      key: 'redemptionType',
      label: 'Redemption Type',
      type: 'custom',
      required: false,
      defaultValue: 'barcode',
      helpText: 'How users redeem this offer',
      format: (value: 'self' | 'barcode' | undefined) => (
        <RedemptionTypeDisplay value={value} />
      ),
      inputFormat: (
        value: 'self' | 'barcode' | undefined,
        onChange: (value: 'self' | 'barcode') => void,
      ) => <RedemptionTypeSelector value={value} onChange={onChange} />,
    },
    {
      key: 'termsAndConditions',
      label: 'Terms and Conditions',
      type: 'richtext',
      required: false,
      placeholder: 'Enter terms and conditions',
      helpText: 'Legal terms displayed to users before redeeming',
    },
    {
      key: 'featuredImageId',
      label: 'Featured Image',
      type: 'image',
      ...mediaIdImageField,
      required: false,
      helpText: 'Image displayed on the offer card in the mobile app',
    },
    {
      key: 'featuredImage',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: PromotionalOffer['featuredImage'], fullRecord?: PromotionalOffer) => {
        return <PromotionalOfferImageCell offer={fullRecord} />;
      },
    },
    {
      key: 'currentRedemptions',
      label: 'Current Redemptions',
      type: 'number',
      readonly: true,
    },
    {
      key: 'usageCount',
      label: 'Usage Count',
      type: 'number',
      readonly: true,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['featuredImage', 'name', 'title', 'status', 'isActive', 'createdAt', 'updatedAt'],
    searchableFields: ['name', 'title', 'description'],
    sortableFields: ['name', 'title', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'createdAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'multiselect',
        options: [
          { label: 'Published', value: 'published' },
          { label: 'Draft', value: 'draft' },
        ],
      },
      {
        key: 'isActive',
        label: 'Is Active',
        type: 'multiselect',
        options: [
          { label: 'Active', value: 'true' },
          { label: 'Inactive', value: 'false' },
        ],
      },
    ],
    actions: {
      create: {
        enabled: true,
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: true,
      },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core promotional offer details',
        fields: ['name', 'title', 'status', 'featuredImageId', 'isActive', 'redemptionType', 'description'],
        defaultExpanded: true,
      },
      {
        title: 'Discount Information',
        description: 'Discount details',
        fields: ['discountType'],
        defaultExpanded: true,
      },
      {
        title: 'Validity Period',
        description: 'When this offer can be redeemed',
        fields: ['validFrom', 'validUntil'],
        defaultExpanded: true,
      },
      {
        title: 'Redemption Limits',
        description: 'How many times users can redeem',
        fields: ['maxUserRedemptions', 'maxRedemptions'],
        defaultExpanded: true,
      },
      {
        title: 'Usage Statistics',
        description: 'Current usage statistics',
        fields: ['currentRedemptions'],
        defaultExpanded: true,
      },
      {
        title: 'Terms and Conditions',
        description: 'Legal and usage terms',
        fields: ['termsAndConditions'],
        defaultExpanded: false,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    createSchema: createPromotionalOfferSchema,
    updateSchema: updatePromotionalOfferSchema,
    sections: [
      {
        title: 'General',
        description: 'Core offer information visible to users',
        fields: ['name', 'title', 'featuredImageId', 'description', 'status', 'isActive', 'redemptionType'],
        columns: 1,
      },
      {
        title: 'Discount',
        description: 'How the discount is applied',
        fields: ['discountType', 'discountPercentage', 'maxDiscountAmount', 'discountValue'],
        columns: 1,
      },
      {
        title: 'Validity Period',
        description: 'When this offer can be redeemed',
        fields: ['validFrom', 'validUntil'],
        columns: 2,
      },
      {
        title: 'Redemption Limits',
        description: 'Control how many times users can redeem',
        fields: ['maxUserRedemptions', 'maxRedemptions'],
        columns: 2,
      },
      {
        title: 'Terms',
        description: 'Terms and Conditions',
        fields: ['termsAndConditions'],
        columns: 1,
      },
    ],
    validation: {
      discountPercentage: {
        custom: (value: number | undefined, formData: PromotionalOffer) => {
          if (formData.discountType === 'percentage' && (!value || value <= 0)) {
            return 'Discount percentage is required when using percentage type';
          }
          return null;
        },
      },
      discountValue: {
        custom: (value: number | undefined, formData: PromotionalOffer) => {
          if (formData.discountType === 'fixed_amount' && (!value || value <= 0)) {
            return 'Discount amount is required when using fixed amount type';
          }
          return null;
        },
      },
    },
  },
};
